export type TableFilterBulkSelectState = 'none' | 'partial' | 'all';

type FilterOption<TValue> = {
  label: string;
  value: TValue;
};

export function isCheckboxMultiSelect(params: { variant?: 'checkbox' | 'radio'; singleSelect?: boolean }): boolean {
  return (params.variant ?? 'checkbox') === 'checkbox' && params.singleSelect !== true;
}

export function shouldShowBulkSelectAction(params: {
  variant?: 'checkbox' | 'radio';
  singleSelect?: boolean;
  availableOptionCount: number;
}): boolean {
  return isCheckboxMultiSelect(params) && params.availableOptionCount > 0;
}

export function getCurrentlyAvailableOptions<TValue>(params: {
  options: ReadonlyArray<FilterOption<TValue>>;
  search: string;
  isRemoteSearch: boolean;
}): Array<FilterOption<TValue>> {
  const { options, search, isRemoteSearch } = params;

  // Remote/typeahead filters already scoped `options` to the currently loaded result set.
  if (isRemoteSearch) {
    return [...options];
  }

  const trimmed = search.trim().toLowerCase();
  if (!trimmed) {
    return [...options];
  }

  return options.filter((option) => option.label.toLowerCase().includes(trimmed));
}

export function getBulkSelectState<TValue>(params: {
  availableValues: ReadonlyArray<TValue>;
  selectedValues: ReadonlyArray<TValue>;
}): TableFilterBulkSelectState {
  const { availableValues, selectedValues } = params;
  if (availableValues.length === 0) {
    return 'none';
  }

  const selectedSet = new Set(selectedValues);
  const selectedAvailableCount = availableValues.filter((value) => selectedSet.has(value)).length;

  if (selectedAvailableCount === 0) {
    return 'none';
  }

  if (selectedAvailableCount === availableValues.length) {
    return 'all';
  }

  return 'partial';
}

export function selectAllAvailableValues<TValue>(params: {
  availableValues: ReadonlyArray<TValue>;
  selectedValues: ReadonlyArray<TValue>;
}): TValue[] {
  const selectedSet = new Set(params.selectedValues);
  const toAdd = params.availableValues.filter((value) => !selectedSet.has(value));
  return [...params.selectedValues, ...toAdd];
}

export function nextBulkSelectValues<TValue>(params: {
  availableValues: ReadonlyArray<TValue>;
  selectedValues: ReadonlyArray<TValue>;
}): TValue[] {
  if (getBulkSelectState(params) === 'all') {
    return [];
  }

  return selectAllAvailableValues(params);
}

export function getGroupedAvailableValues<TValue, TChildValue>(params: {
  options: ReadonlyArray<{
    label: string;
    value: TValue;
    children?: ReadonlyArray<{ label: string; value: TChildValue }>;
  }>;
  search: string;
}): { parentValues: TValue[]; childValues: TChildValue[] } {
  const trimmed = params.search.trim().toLowerCase();
  const parentValues: TValue[] = [];
  const childValues: TChildValue[] = [];

  for (const option of params.options) {
    const children = option.children ?? [];

    if (!trimmed) {
      parentValues.push(option.value);
      for (const child of children) {
        childValues.push(child.value);
      }
      continue;
    }

    const parentMatches = option.label.toLowerCase().includes(trimmed);
    if (parentMatches) {
      // Same as clicking the parent: select it and every child.
      parentValues.push(option.value);
      for (const child of children) {
        childValues.push(child.value);
      }
      continue;
    }

    for (const child of children) {
      if (child.label.toLowerCase().includes(trimmed)) {
        childValues.push(child.value);
      }
    }
  }

  return { parentValues, childValues };
}

export function getGroupedBulkSelectState<TValue, TChildValue>(params: {
  availableParentValues: ReadonlyArray<TValue>;
  availableChildValues: ReadonlyArray<TChildValue>;
  selectedParentValues: ReadonlyArray<TValue>;
  selectedChildValues: ReadonlyArray<TChildValue>;
}): TableFilterBulkSelectState {
  const availableCount = params.availableParentValues.length + params.availableChildValues.length;
  if (availableCount === 0) {
    return 'none';
  }

  const selectedParents = new Set(params.selectedParentValues);
  const selectedChildren = new Set(params.selectedChildValues);
  const selectedAvailableCount =
    params.availableParentValues.filter((value) => selectedParents.has(value)).length +
    params.availableChildValues.filter((value) => selectedChildren.has(value)).length;

  if (selectedAvailableCount === 0) {
    return 'none';
  }

  if (selectedAvailableCount === availableCount) {
    return 'all';
  }

  return 'partial';
}

export function nextGroupedBulkSelectValues<TValue, TChildValue>(params: {
  availableParentValues: ReadonlyArray<TValue>;
  availableChildValues: ReadonlyArray<TChildValue>;
  selectedParentValues: ReadonlyArray<TValue>;
  selectedChildValues: ReadonlyArray<TChildValue>;
}): { parentValues: TValue[]; childValues: TChildValue[] } {
  if (getGroupedBulkSelectState(params) === 'all') {
    return { parentValues: [], childValues: [] };
  }

  return {
    parentValues: selectAllAvailableValues({
      availableValues: params.availableParentValues,
      selectedValues: params.selectedParentValues,
    }),
    childValues: selectAllAvailableValues({
      availableValues: params.availableChildValues,
      selectedValues: params.selectedChildValues,
    }),
  };
}
